import {
  useMutation,
  useQuery,
  UseQueryOptions,
  QueryKey,
  useQueryClient,
} from "@tanstack/react-query";
import getBITSEndApiMap from "../apiMap";
import { api } from "../services/AxiosService";

export interface CreateDatasetResponse {
  isSuccess?: boolean;
  message?: string;
  data?: any;
}

export async function createDataset(
  fd: FormData,
): Promise<CreateDatasetResponse> {
  const url = getBITSEndApiMap("createDataset");
  const { data } = await api.post<CreateDatasetResponse>(url, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export function useCreateDatasetMutation() {
  return useMutation({
    mutationFn: createDataset,
  });
}

// Dataset interfaces
export interface EnhancedDataset {
  id: string;
  title: string;
  description?: string;
  visibility: "PRIVATE" | "COURSE" | "PUBLIC" | "PROJECT";
  tags: string[];
  fileName?: string;
  fileSize?: number;
  fileType?: string;
  coverPhotoUrl?: string;
  createdAt: string;
  created_at: string;
  updatedAt: string;
  updated_at: string;
  uploader?: {
    name: string;
  };
  files?: any[];
  version?: string;
  projects?: any[];
  context?: string;
  createdBy: {
    id: string;
    name: string;
    email: string;
  };
  course?: {
    id: string;
    name: string;
  };
  usageCount?: number;
  status?: string;
  difficultyLevel?: string;
  filesCount?: number;
  commentsCount?: number;
  submissionCount?: number;
  dueDate?: string;
  ltiResourceLinks?: any[];
  isLinked?: boolean;
  s3_keys?: Array<{
    key: string;
    originalName?: string;
    size?: number;
  }>;
  _markedForDeletion?: boolean; // Internal flag for UI state
}

export interface EnhancedDatasetsPagination {
  page: number; // Current page (backend uses "page")
  total_pages: number; // Total pages (backend uses "total_pages")
  total: number; // Total items (backend uses "total")
  limit: number; // Items per page (backend uses "limit")
}

export interface EnhancedDatasetsResponseData {
  datasets: EnhancedDataset[];
  pagination: EnhancedDatasetsPagination;
}

export interface EnhancedDatasetsResponse {
  isSuccess: boolean;
  message: string;
  data: EnhancedDatasetsResponseData;
}

export interface DatasetResponse {
  isSuccess: boolean;
  message: string;
  data: EnhancedDataset;
}

export interface GetEnhancedDatasetsParams {
  page?: number;
  limit?: number;
  search?: string;
  visibility?: "PRIVATE" | "COURSE" | "PUBLIC";
  createdBy?: string;
  courseId?: string;
  tags?: string[];
}

export async function getEnhancedDatasets(
  params: GetEnhancedDatasetsParams = {},
): Promise<EnhancedDatasetsResponseData> {
  const url = getBITSEndApiMap("getDatasets");
  const { data } = await api.get<EnhancedDatasetsResponse>(url, {
    params,
  });
  return data.data;
}

export function useEnhancedDatasetsQuery(
  params: GetEnhancedDatasetsParams,
  options?: Partial<
    UseQueryOptions<
      EnhancedDatasetsResponseData,
      Error,
      EnhancedDatasetsResponseData,
      QueryKey
    >
  >,
) {
  return useQuery<
    EnhancedDatasetsResponseData,
    Error,
    EnhancedDatasetsResponseData,
    QueryKey
  >({
    queryKey: ["datasets", "enhanced", params] as QueryKey,
    queryFn: () => getEnhancedDatasets(params),
    ...(options as any),
  });
}

// Link Dataset to Project interfaces
export interface LinkDatasetRequest {
  projectId: string;
  datasetIds: string[]; // Updated to support multiple datasets
}

export interface LinkDatasetResponse {
  isSuccess: boolean;
  message: string;
  data?: any;
}

export async function linkDataset(
  payload: LinkDatasetRequest,
): Promise<LinkDatasetResponse> {
  const url = getBITSEndApiMap("linkDataset");
  const { data } = await api.post<LinkDatasetResponse>(url, payload);
  return data;
}

export function useLinkDatasetMutation() {
  return useMutation({
    mutationFn: linkDataset,
  });
}

// Unlink Dataset from Project interfaces
export interface UnlinkDatasetRequest {
  datasetId: string;
  projectId: string;
}

export interface UnlinkDatasetResponse {
  isSuccess: boolean;
  message: string;
  data?: any;
}

export async function unlinkDataset(
  payload: UnlinkDatasetRequest,
): Promise<UnlinkDatasetResponse> {
  const url = getBITSEndApiMap("unlinkDataset");
  const { data } = await api.post<UnlinkDatasetResponse>(url, payload);
  return data;
}

export function useUnlinkDatasetMutation() {
  return useMutation({
    mutationFn: unlinkDataset,
  });
}

// Get dataset by ID
export async function getDatasetById(id: string): Promise<EnhancedDataset> {
  const url = getBITSEndApiMap("getDatasetById").replace(":id", id);
  const { data } = await api.get<DatasetResponse>(url);
  return data.data;
}

export function useDatasetByIdQuery(
  id: string,
  options?: Partial<
    UseQueryOptions<EnhancedDataset, Error, EnhancedDataset, QueryKey>
  >,
) {
  return useQuery<EnhancedDataset, Error, EnhancedDataset, QueryKey>({
    queryKey: ["dataset", id] as QueryKey,
    queryFn: () => getDatasetById(id),
    enabled: !!id,
    ...(options as any),
  });
}

// Update Dataset interfaces
export interface UpdateDatasetPayload {
  title?: string;
  description?: string;
  visibility?: "PRIVATE" | "PROJECT" | "PUBLIC";
  tags?: string[];
  filesToDelete?: string[];
  datasetFile?: File[];
  coverPhoto?: File | null;
  removeCoverPhoto?: boolean;
}

export interface UpdateDatasetResponse {
  success: boolean;
  message: string;
  data: {
    id: string;
    title: string;
    description?: string;
    s3_keys: any[];
    visibility: string;
    tags: string[];
    operation_summary: {
      files_deleted_count: number;
      files_added_count: number;
      total_files: number;
      metadata_updated: boolean;
      deleted_files: string[];
      added_files: string[];
    };
  };
}

export async function updateDataset(
  id: string,
  payload: UpdateDatasetPayload,
): Promise<UpdateDatasetResponse> {
  const url = getBITSEndApiMap("getDatasetById").replace(":id", id);
  const formData = new FormData();

  // Add metadata fields if provided
  if (payload.title) formData.append("title", payload.title);
  if (payload.description) formData.append("description", payload.description);
  if (payload.visibility) formData.append("visibility", payload.visibility);

  // Add tags as JSON string
  if (payload.tags && payload.tags.length > 0) {
    formData.append("tags", JSON.stringify(payload.tags));
  }

  // Add files to delete as JSON string
  if (payload.filesToDelete && payload.filesToDelete.length > 0) {
    formData.append("filesToDelete", JSON.stringify(payload.filesToDelete));
  }

  // Add new dataset files
  if (payload.datasetFile && payload.datasetFile.length > 0) {
    payload.datasetFile.forEach((file) => {
      formData.append("datasetFile", file);
    });
  }

  // Add cover photo
  if (payload.coverPhoto) {
    formData.append("coverPhoto", payload.coverPhoto);
  }

  // Add removeCoverPhoto flag
  if (payload.removeCoverPhoto) {
    formData.append("removeCoverPhoto", "true");
  }

  const { data } = await api.patch<UpdateDatasetResponse>(url, formData);
  return data;
}

export function useUpdateDatasetMutation() {
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdateDatasetPayload;
    }) => updateDataset(id, payload),
  });
}

// Delete dataset by ID
export async function deleteDataset(id: string): Promise<void> {
  const url = getBITSEndApiMap("getDatasetById").replace(":id", id);
  await api.delete(url);
}

export function useDeleteDataset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteDataset(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["datasets"] });
      queryClient.invalidateQueries({ queryKey: ["dataset"] });
    },
    onError: (error) => {
      console.error("Delete dataset failed:", error);
    },
  });
}
